import { useEffect, useState } from "react";
import { featuresApi } from "../api/featuresApi";
import {
  ComboBox,
  DrawerTabs,
  EntityDiscussionsTab,
  EntityAttachmentsTab,
} from "../../common/components";
import { ASSET_OPTIONS } from "../constants/featuresConstants";
import "../../../V2SideDrawer.css";
import "../../../MasterViewV2.css";
import "../../../DefectsViewV2.css";

const EMPTY_FEATURE_FORM = {
  product_id: "",
  feature_code: "",
  feature_name: "",
  description: "",
  applicable_asset: [],
  latest_version: "",
  repository: "",
  document_url: "",
  effort_estimated_hours: 8,
  effort_estimated_minutes: 0,
  effort_phase: "",
  effort_notes: "",
};

function mapFeatureToForm(feature) {
  if (!feature) return EMPTY_FEATURE_FORM;
  return {
    ...EMPTY_FEATURE_FORM,
    ...feature,
    applicable_asset: Array.isArray(feature.applicable_asset) ? feature.applicable_asset : [],
  };
}

export default function FeatureDrawer({ mode = "create", feature, products = [], onClose, onSaved, showToast }) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState(() => mapFeatureToForm(feature));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");

  // Re-sync form when the parent passes in a different feature
  useEffect(() => {
    setForm(mapFeatureToForm(feature));
  }, [feature?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handlePrefixChange = (raw) => {
    const value = String(raw || "").replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 4);
    set("feature_code", value);
  };

  const toggleAsset = (a) => {
    setForm((f) => ({
      ...f,
      applicable_asset: f.applicable_asset.includes(a)
        ? f.applicable_asset.filter((x) => x !== a)
        : [...f.applicable_asset, a],
    }));
  };

  const productOptions = products.map((p) => ({ value: p.id, label: p.name }));

  async function handleSave() {
    setError("");
    if (!form.product_id) {
      setError("Product is required.");
      return;
    }
    if (!isEdit && form.feature_code.length < 2) {
      setError("Module prefix must be 2–4 letters.");
      return;
    }
    if (!form.feature_name.trim()) {
      setError("Feature name is required.");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await featuresApi.updateFeature(feature.id, form);
        showToast?.("Feature updated", "success");
      } else {
        await featuresApi.createFeature(form);
        showToast?.("Feature created", "success");
      }
      onSaved?.();
    } catch (err) {
      setError(err?.message || "Failed to save feature");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="v2drw-scrim" onClick={onClose} />
      <div className="v2drw ftv2-drw" role="dialog" aria-modal="true">
        <div className="v2drw-head">
          <div className="v2drw-head-main">
            <div className="v2drw-eyebrow">Features</div>
            <div className="v2drw-title">
              {isEdit ? (form.feature_name || "Edit feature") : "New feature"}
            </div>
            {isEdit && form.feature_code && (
              <div className="v2drw-meta">
                <span className="ftv2-code ftv2-code--feature">{form.feature_code}</span>
              </div>
            )}
          </div>
          <button className="v2drw-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {error && <div className="v2drw-msg warn">{error}</div>}

        {isEdit && (
          <DrawerTabs
            tabs={[
              { key: "overview",    label: "Overview",    icon: "overview"    },
              { key: "discussions", label: "Discussions", icon: "discussions" },
              { key: "attachments", label: "Attachments", icon: "attachments" },
            ]}
            activeTab={tab}
            onChange={setTab}
          />
        )}

        <div className="v2drw-body">
          {(!isEdit || tab === "overview") && (
          <div className="ftv2-form">
            <div className="ftv2-form-row">
              <div className="ftv2-form-fld">
                <label className="ftv2-form-label">Product *</label>
                <ComboBox
                  options={productOptions}
                  value={form.product_id}
                  onChange={(v) => set("product_id", v)}
                  placeholder="Search product…"
                />
              </div>
              <div className="ftv2-form-fld">
                {isEdit ? (
                  <>
                    <label className="ftv2-form-label">Feature Code</label>
                    <div className="ftv2-readonly">
                      <span className="ftv2-code ftv2-code--feature">{form.feature_code || "—"}</span>
                      <span className="ftv2-readonly-note">Code cannot be changed</span>
                    </div>
                  </>
                ) : (
                  <>
                    <label className="ftv2-form-label">
                      Module Prefix *
                      <span className="ftv2-label-hint">2–4 letters · e.g. AUTH, BO, MW</span>
                    </label>
                    <div className="ftv2-prefix">
                      <input
                        className="ftv2-form-input ftv2-prefix-input"
                        value={form.feature_code}
                        onChange={(e) => handlePrefixChange(e.target.value)}
                        placeholder="AUTH"
                        maxLength={4}
                        spellCheck={false}
                      />
                      <span className={`ftv2-prefix-preview${form.feature_code.length >= 2 ? " is-ready" : ""}`}>
                        {form.feature_code.length >= 2 ? `${form.feature_code}-` : "??-"}
                        <span className="seq">0001</span>
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="ftv2-form-fld">
              <label className="ftv2-form-label">Feature Name *</label>
              <input
                className="ftv2-form-input"
                value={form.feature_name}
                onChange={(e) => set("feature_name", e.target.value)}
                placeholder="e.g. Authentication"
                autoFocus={!isEdit}
              />
            </div>

            <div className="ftv2-form-fld">
              <label className="ftv2-form-label">Description</label>
              <textarea
                className="ftv2-form-textarea"
                value={form.description}
                onChange={(e) => set("description", e.target.value)}
                rows={3}
                placeholder="Brief description…"
              />
            </div>

            <div className="ftv2-form-row">
              <div className="ftv2-form-fld">
                <label className="ftv2-form-label">Latest Version</label>
                <input
                  className="ftv2-form-input"
                  value={form.latest_version}
                  onChange={(e) => set("latest_version", e.target.value)}
                  placeholder="e.g. v2.1.0"
                />
              </div>
              <div className="ftv2-form-fld">
                <label className="ftv2-form-label">Repository URL</label>
                <input
                  className="ftv2-form-input"
                  value={form.repository}
                  onChange={(e) => set("repository", e.target.value)}
                  placeholder="https://github.com/…"
                />
              </div>
            </div>

            <div className="ftv2-form-fld">
              <label className="ftv2-form-label">Document URL</label>
              <input
                className="ftv2-form-input"
                value={form.document_url}
                onChange={(e) => set("document_url", e.target.value)}
                placeholder="https://docs.example.com/…"
              />
            </div>

            <div className="ftv2-form-fld">
              <label className="ftv2-form-label">Applicable Assets</label>
              <div className="ftv2-chip-toggles">
                {ASSET_OPTIONS.map((a) => {
                  const on = form.applicable_asset.includes(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      className={`ftv2-chip-toggle${on ? " is-on" : ""}`}
                      onClick={() => toggleAsset(a)}
                    >
                      {a}
                    </button>
                  );
                })}
              </div>
            </div>

            {!isEdit && (
              <div className="ftv2-form-row">
                <div className="ftv2-form-fld">
                  <label className="ftv2-form-label">
                    Est. Hours
                    <span className="ftv2-label-hint">0–160</span>
                  </label>
                  <input
                    type="number"
                    className="ftv2-form-input"
                    min={0}
                    max={160}
                    step={1}
                    value={form.effort_estimated_hours}
                    onChange={(e) => set("effort_estimated_hours",
                      Math.min(160, Math.max(0, parseInt(e.target.value) || 0))
                    )}
                  />
                </div>
                <div className="ftv2-form-fld">
                  <label className="ftv2-form-label">
                    Est. Minutes
                    <span className="ftv2-label-hint">0–59</span>
                  </label>
                  <input
                    type="number"
                    className="ftv2-form-input"
                    min={0}
                    max={59}
                    step={1}
                    value={form.effort_estimated_minutes}
                    onChange={(e) => set("effort_estimated_minutes",
                      Math.min(59, Math.max(0, parseInt(e.target.value) || 0))
                    )}
                  />
                </div>
              </div>
            )}
          </div>
          )}

          {isEdit && tab === "discussions" && (
            <EntityDiscussionsTab
              entityType="feature"
              entityId={feature?.id}
              showToast={showToast}
            />
          )}

          {isEdit && tab === "attachments" && (
            <EntityAttachmentsTab
              refType="feature"
              refId={feature?.id}
              caption="Feature"
              showToast={showToast}
            />
          )}
        </div>

        <div className="v2drw-foot">
          <span className="v2drw-foot-spacer" />
          <button className="v2drw-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          {(!isEdit || tab === "overview") && (
            <button className="v2drw-btn-save" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create feature"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
